import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import MarketingNav from '../components/MarketingNav';
import { ArrowRight, Check, X, Zap, Crown, Star, Mail } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { useRef, useEffect } from 'react';

function useScrollAnimate() {
  const ref = useRef(null);
  useEffect(() => { const el = ref.current; if (!el) return; const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { el.style.opacity = '1'; el.style.transform = 'translateY(0)'; } }, { threshold: 0.1 }); obs.observe(el); return () => obs.disconnect(); }, []);
  return ref;
}
function Reveal({ children, className = '', delay = 0 }) {
  const ref = useScrollAnimate();
  return <div ref={ref} className={className} style={{ opacity: 0, transform: 'translateY(32px)', transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s` }}>{children}</div>;
}

const PRICING = [
  { name: 'Pro', price: '69', period: '/month', desc: 'Everything unlimited. No limits.', icon: Zap,
    features: ['Unlimited products', 'Unlimited landing pages', 'Unlimited checkout forms', 'Sales analytics & reports', 'Instant file delivery', 'Multi-gateway checkout', 'Customer management', 'Facebook & TikTok pixel tracking', 'Embeddable checkout forms'],
    notIncluded: ['AI Landing Page Builder', 'AI Digital Studio', 'AI Growth Consultant'],
    cta: 'Start 7-Day Free Trial', popular: false, ctaAction: 'register' },
  { name: 'Premium', price: '99', period: '/month', desc: 'Unlimited + full AI access', icon: Crown,
    features: ['Everything in Pro', 'AI Landing Page Builder', 'AI Digital Studio', 'AI Growth Consultant', 'AI Copywriting', 'Priority support', 'Custom domain'],
    notIncluded: [],
    cta: 'Start Premium', popular: true, ctaAction: 'register' },
  { name: 'Custom', price: '999', period: '', desc: 'Everything done for you', icon: Star,
    features: ['Everything in Premium', 'Custom page design', 'Product setup & migration', 'Payment gateway setup', 'Dedicated account manager', 'Priority onboarding', '1-on-1 strategy session'],
    notIncluded: [],
    cta: 'Contact Us', popular: false, ctaAction: 'contact' },
];

const FAQS = [
  { q: 'Can I try before I pay?', a: 'Yes. Use a promo code to get free access and explore the platform. Upgrade to Pro or Premium when you\'re ready.' },
  { q: 'Is there a transaction fee?', a: 'Funnely charges 0% platform fee. You only pay your payment gateway\'s processing fees.' },
  { q: 'Can I cancel anytime?', a: 'Yes. No lock-in contracts. Cancel with one click from your dashboard.' },
  { q: 'What payment gateways are supported?', a: 'Billplz, ToyyibPay, SenangPay, Stripe (international), Midtrans, and Xendit (Indonesia).' },
  { q: 'What\'s included in the Custom plan?', a: 'We handle everything — page design, product upload, gateway setup, and onboarding. You just approve and launch.' },
  { q: 'Can I switch plans later?', a: 'Absolutely. Upgrade or downgrade anytime. Changes take effect immediately.' },
];

export default function PricingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ fontFamily: 'Lexend, sans-serif' }}>

      {/* Nav */}
      <MarketingNav activePage="/pricing" />

      {/* Hero */}
      <section className="relative pt-28 pb-16 sm:pt-36 sm:pb-20 px-4 overflow-hidden" style={{ backgroundColor: '#195259' }}>
        <img src="/images/marketing/element-teal-right.png" alt="" className="absolute top-0 right-0 w-[140px] sm:w-[200px] lg:w-[280px] h-auto pointer-events-none select-none" style={{ zIndex: 1, opacity: 0.5 }} />
        <img src="/images/marketing/element-teal-left.png" alt="" className="absolute bottom-0 left-0 w-[100px] sm:w-[160px] lg:w-[220px] h-auto pointer-events-none select-none" style={{ zIndex: 1, opacity: 0.5 }} />
        <div className="max-w-4xl mx-auto text-center relative" style={{ zIndex: 2 }}>
          <Reveal>
            <p className="text-sm font-semibold uppercase tracking-[3px] mb-4" style={{ color: '#FF9A00' }}>Pricing</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight mb-6">
              Transparent pricing.<br /><span style={{ color: '#FF9A00' }}>No surprises.</span>
            </h1>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: '#F5E9CF', opacity: 0.8 }}>
              No hidden fees. No platform transaction charges. Pick a plan and start selling today.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 sm:py-28 px-4" style={{ backgroundColor: '#F5E9CF' }}>
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {PRICING.map((plan, pi) => {
              const Icon = plan.icon;
              return (
                <Reveal key={plan.name} delay={pi * 0.1}>
                  <div className={`rounded-2xl p-7 relative bg-white h-full flex flex-col ${plan.popular ? 'scale-105 shadow-xl' : 'shadow-lg'}`} style={{ border: `1.5px solid ${plan.popular ? '#FF9A00' : '#195259'}` }}>
                    {plan.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2"><Badge className="px-4 text-white font-semibold" style={{ backgroundColor: '#FF9A00' }}>Most Popular</Badge></div>}
                    <div className="flex items-center gap-3 mb-1">
                      <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: plan.popular ? '#FF9A00' : '#195259' }}>
                        <Icon className="h-5 w-5" style={{ color: plan.popular ? '#195259' : 'white' }} />
                      </div>
                      <h3 className="text-xl font-bold" style={{ color: '#195259' }}>{plan.name}</h3>
                    </div>
                    <p className="text-xs mb-4" style={{ color: '#6D6E71' }}>{plan.desc}</p>
                    <div className="mb-5">
                      {plan.name === 'Custom' ? (
                        <><span className="text-sm" style={{ color: '#6D6E71' }}>Starting from </span><span className="text-4xl font-extrabold" style={{ color: '#195259' }}>RM {plan.price}</span></>
                      ) : (
                        <><span className="text-4xl font-extrabold" style={{ color: '#195259' }}>RM {plan.price}</span><span className="text-sm" style={{ color: '#6D6E71' }}>{plan.period}</span></>
                      )}
                    </div>
                    <Button className="w-full mb-5 h-12 font-semibold rounded-xl text-sm" style={plan.popular ? { backgroundColor: '#FF9A00', color: '#195259' } : { backgroundColor: '#195259', color: 'white' }} onClick={() => plan.ctaAction === 'contact' ? window.open('mailto:hello@myfunnely.com', '_blank') : navigate('/register')}>
                      {plan.cta} {plan.ctaAction !== 'contact' && <ArrowRight className="h-4 w-4 ml-1.5" />}
                    </Button>
                    <div className="space-y-2.5 flex-1">
                      {plan.features.map(f => <div key={f} className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 shrink-0" style={{ color: '#FF9A00' }} /><span style={{ color: '#195259' }}>{f}</span></div>)}
                      {plan.notIncluded.map(f => <div key={f} className="flex items-center gap-2 text-sm"><X className="h-4 w-4 shrink-0 text-gray-300" /><span className="text-gray-400">{f}</span></div>)}
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Comparison note */}
          <Reveal>
            <div className="mt-16 text-center">
              <p className="text-sm" style={{ color: '#6D6E71' }}>All plans include: Secure file delivery, customer management, sales analytics, and 6 payment gateway options.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 sm:py-28 px-4 bg-white">
        <div className="max-w-2xl mx-auto">
          <Reveal>
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: '#195259' }}>Pricing FAQ</h2>
            </div>
          </Reveal>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <Reveal key={faq.q} delay={Math.min(i * 0.05, 0.2)}>
                <details className="rounded-xl overflow-hidden bg-white group" style={{ border: '1.5px solid #195259', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                  <summary className="flex items-center justify-between p-5 cursor-pointer hover:bg-[#F5E9CF]/40 transition-colors list-none">
                    <span className="font-semibold text-sm" style={{ color: '#195259' }}>{faq.q}</span>
                    <span className="text-lg" style={{ color: '#FF9A00' }}>+</span>
                  </summary>
                  <div className="px-5 pb-5"><p className="text-sm leading-relaxed" style={{ color: '#6D6E71' }}>{faq.a}</p></div>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32 px-4" style={{ backgroundColor: '#195259' }}>
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <img src="/images/marketing/cta-mascot.png" alt="" loading="lazy" className="w-[200px] sm:w-[260px] h-auto mx-auto mb-8 object-contain" style={{ animation: 'float 4s ease-in-out infinite' }} />
            <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white mb-5">Ready to start selling?</h2>
            <p className="text-lg mb-10" style={{ color: '#F5E9CF', opacity: 0.8 }}>Pick a plan and launch your digital store today.</p>
            <Button size="lg" className="text-base px-12 h-14 font-semibold text-[#195259] rounded-full shadow-lg hover:shadow-xl transition-shadow" style={{ backgroundColor: '#FF9A00' }} onClick={() => navigate('/register')}>
              Create Your Store Now <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </Reveal>
        </div>
      </section>

      <style>{`@keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }`}</style>

      {/* Footer */}
      <footer className="py-10 px-4" style={{ backgroundColor: '#0F3338' }}>
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div><img src="/logos/funnely-white.png" alt="Funnely" className="h-5 object-contain" /><p className="text-xs mt-1" style={{ color: '#6D6E71' }}>The easiest way to sell digital products.</p></div>
          <div className="flex items-center gap-6 text-sm" style={{ color: '#6D6E71' }}>
            <a href="/features" className="hover:text-white transition-colors">Features</a>
            <a href="/how-it-works" className="hover:text-white transition-colors">How It Works</a>
            <a href="/login" className="hover:text-white transition-colors">Log In</a>
          </div>
          <p className="text-xs" style={{ color: '#6D6E71' }}>&copy; 2026 Funnely. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
